/**
 * FinancialsSheet — iter-259 · iter-alignment P50 (Commission Type)
 * ─────────────────────────────────────────────────────────────
 * Lightweight right-side drawer for editing Deal Room financial
 * fields. Strict refinement — NOT a finance app, NOT an
 * accounting workflow. Just the canonical operational fields the
 * Financials card surfaces.
 *
 * iter-alignment · P50 — Commission Type.  The independently
 * editable "Agreed fee (£)" + "Fee %" pair is replaced with a
 * segmented Commission Type control:
 *   • Percentage — user edits Agreed Sale Price + Commission (%);
 *     Commission (£) is auto-calculated (price × pct) and shown
 *     read-only.
 *   • Fixed Fee — user edits Agreed Sale Price + Fixed Fee (£);
 *     the % / calculated fields are hidden.
 *
 * The canonical `agreedFee` (persisted as `deal.fee`) remains the
 * single source of truth for every downstream surface — Forecasting
 * Centre, Right Rail, dashboards, reporting and analytics all
 * continue to read `deal.fee` and NOTHING duplicates the
 * commission-calculation logic.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";
import { Coins } from "lucide-react";

const RISK_OPTIONS = ["Low", "Medium", "High"];
const COMMISSION_TYPES = [
  { key: "percentage", label: "Percentage" },
  { key: "fixed",      label: "Fixed Fee"  },
];

const _parseNum = (raw) => {
  if (raw == null || raw === "") return null;
  const num = parseFloat(String(raw).replace(/[^0-9.]/g, ""));
  return isNaN(num) ? null : num;
};

/* iter-alignment · P0 · Currency Formatting SSOT (2026-02-19)
 * — canonical UK-thousands formatter for currency INPUTS.  Takes
 * ANY raw string ("1400000", "£1,400,000", "1,400,000"), strips
 * to bare digits, and re-renders with UK commas ("1,400,000").
 * Returns "" for empty / non-numeric input so the placeholder
 * still shows.  Underlying `form.*` state stays comma-formatted
 * for display; the save handler already normalises to a bare
 * integer via `.replace(/[^0-9]/g, "")`.  Declared at module top
 * so it is fully initialised before either `FinancialsSheet` (the
 * hydration `useEffect` calls it on open) or `Field` (the
 * currency variant fans keystrokes through it) are constructed —
 * no temporal-dead-zone hazard in any bundler. */
const _formatCurrencyInput = (raw) => {
  if (raw == null) return "";
  const digits = String(raw).replace(/[^0-9]/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("en-GB");
};

/* Nearest-penny rounding for the derived commission £.  Single
 * source of truth for the calculation — every downstream surface
 * still reads `deal.fee` so this helper never gets duplicated
 * elsewhere. */
const _computeCommissionGbp = (priceRaw, pctRaw) => {
  const price = _parseNum(priceRaw);
  const pct = _parseNum(pctRaw);
  if (price == null || pct == null) return null;
  return Math.round(price * pct) / 100;    // (price × pct/100) → 2dp pennies
};

/* UK currency formatting — read-only display for the derived
 * Commission (£) in Percentage mode. */
const _fmtGbp = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return "";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(n));
  } catch (_e) {
    return `£${Number(n).toFixed(2)}`;
  }
};

const _toMonthInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  } catch (_e) {
    return "";
  }
};

const _fromMonthInput = (val) => {
  if (!val) return null;
  const [y, m] = val.split("-");
  if (!y || !m) return null;
  return `${y}-${m}-15`;
};

/* Legacy deals open in the appropriate mode WITHOUT touching
 * their current revenue: an explicit `commissionType` field wins;
 * otherwise presence of `feePct` implies percentage mode; anything
 * else falls back to fixed-fee. */
const _detectMode = (deal) => {
  if (!deal) return "percentage";
  const explicit = String(deal.commissionType || "").toLowerCase();
  if (explicit === "percentage" || explicit === "fixed") return explicit;
  if (deal.feePct != null && deal.feePct !== "") return "percentage";
  return "fixed";
};

export function FinancialsSheet({ open, onOpenChange, deal, onSave }) {
  const [form, setForm] = useState({
    commissionType:     "percentage",
    agreedPrice:        "",
    // Percentage-mode draft (preserved across mode switches while
    // the drawer is open — never overwrites the other mode).
    commissionPct:      "",
    // Fixed-fee-mode draft (preserved across mode switches).
    fixedFee:           "",
    vatIncluded:        false,
    expectedExchangeMonth: "",
    // iter-alignment · P0 · Financials Refinement (2026-02-28) —
    // Forecast Confidence was retired as a MANUAL editor input.
    // The backend field remains accepted so historic values and
    // Forecasting Centre inputs are untouched (see `handleSave`
    // where the patch NO LONGER emits `forecastConfidence` — the
    // stored value on the deal is left as-is).
    revenueAtRisk:      "Low",
  });

  // Re-hydrate the form whenever the sheet opens.
  useEffect(() => {
    if (!open || !deal) return;
    const mode = _detectMode(deal);
    setForm({
      commissionType: mode,
      agreedPrice: _formatCurrencyInput(deal.agreedPrice || deal.agreed_price || deal.price || ""),
      /* iter-alignment · P0 · Financials Refinement (2026-02-28)
       * Commission (%) is displayed with 2-decimal precision so
       * `1.25` reads as `1.25`, `1` reads as `1.00`.  Exact stored
       * value is preserved — we only zero-pad the display.  The
       * step="0.05" control on the input enforces 5-basis-point
       * increments for keyboard/spinner interaction. */
      commissionPct: deal.feePct != null && deal.feePct !== ""
        ? Number(deal.feePct).toFixed(2)
        : "",
      // In percentage mode we keep the fixed-fee draft blank so an
      // operator can switch and type freely without a stale value
      // appearing.  In fixed-fee mode the existing `deal.fee`
      // seeds the fixed-fee input.
      fixedFee: mode === "fixed" && deal.fee != null ? _formatCurrencyInput(deal.fee) : "",
      vatIncluded: deal.vatIncluded === true,
      expectedExchangeMonth: _toMonthInput(
        deal.expectedExchangeMonth || deal.targetExchange || deal.target_exchange
      ),
      revenueAtRisk: deal.revenueAtRisk || "Low",
    });
  }, [open, deal]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isPct = form.commissionType === "percentage";

  /* Derived commission £ — Percentage mode only.  Reuses the
   * canonical `_computeCommissionGbp` helper defined above; no
   * downstream surface duplicates this arithmetic. */
  const derivedCommissionGbp = useMemo(
    () => (isPct ? _computeCommissionGbp(form.agreedPrice, form.commissionPct) : null),
    [isPct, form.agreedPrice, form.commissionPct],
  );

  /* Validation.  Save disabled until the active-mode inputs are
   * numeric AND non-negative.  Empty inactive-mode drafts are
   * ignored so switching modes never blocks Save. */
  const isValid = useMemo(() => {
    const price = _parseNum(form.agreedPrice);
    if (form.agreedPrice !== "" && (price == null || price < 0)) return false;
    if (isPct) {
      const pct = _parseNum(form.commissionPct);
      if (form.commissionPct === "" || pct == null || pct < 0 || pct > 100) return false;
    } else {
      const fee = _parseNum(form.fixedFee);
      if (form.fixedFee === "" || fee == null || fee < 0) return false;
    }
    return true;
  }, [form.agreedPrice, form.commissionPct, form.fixedFee, isPct]);

  const handleSave = () => {
    if (!isValid) return;
    /* Only save the ACTIVE mode's values — inactive fields are
     * intentionally omitted so the store's shallow-merge patch
     * never overwrites existing financial data. */
    /* iter-alignment · P3 · Revenue Persistence Fix (2026-02-28)
     * Coerce every numeric revenue field to a STRING before
     * emitting.  The backend `DealUpdate` schema enforces
     * `fee: Optional[str]` + `agreed_price: Optional[str]`
     * (strict validation — Pydantic v2 does NOT coerce numbers
     * to strings out of the box) so a raw `Number` would 422 on
     * the wire and the FinancialsSheet would appear to save
     * successfully in the UI (optimistic patch lands) but
     * silently roll back on the next refresh.  `_asMoneyStr`
     * normalises to a positive integer-pounds string exactly the
     * way the backend `_validate_money` helper expects. */
    const _asMoneyStr = (n) => {
      if (n == null) return null;
      const num = Number(n);
      if (!Number.isFinite(num) || num <= 0) return null;
      return String(Math.round(num));
    };
    const patch = {
      agreedPrice: form.agreedPrice ? String(form.agreedPrice).replace(/[^0-9]/g, "") || null : null,
      commissionType: form.commissionType,
      vatIncluded: !!form.vatIncluded,
      expectedExchangeMonth: _fromMonthInput(form.expectedExchangeMonth),
      // iter-alignment · P0 · Financials Refinement (2026-02-28) —
      // `forecastConfidence` is no longer emitted from the editor.
      // The backend schema still accepts the field so any historic
      // value on the deal (or Forecasting Centre patch) stays put;
      // omitting it from this patch means our shallow-merge save
      // will NOT overwrite the stored value.
      revenueAtRisk: form.revenueAtRisk,
    };
    if (isPct) {
      patch.feePct = _parseNum(form.commissionPct);
      // iter-alignment · P0 · Fee Basis Integrity (2026-02-28)
      // -----------------------------------------------------
      // Fee Basis Integrity — Fixed Fee and Percentage Fee are
      // mutually exclusive on the SSOT.  When saving a percentage
      // basis, the previously-stored fixed £ fee is cleared so
      // `_feeFor()` always derives the £ figure from
      // `agreedPrice × feePct × 1.20` (which is exactly what the
      // Revenue card already computes on the client via
      // `_computeCommissionGbp`).  Any stale value from a prior
      // fixed-fee save cannot drift the derived total anymore.
      patch.fee = null;
    } else {
      patch.fee = _asMoneyStr(_parseNum(form.fixedFee));
      // iter-alignment · P0 · Fee Basis Integrity (2026-02-28)
      // Symmetrical clear — a saved Fixed Fee wipes the
      // percentage rate so `_feeFor()`'s "use stored fee" branch
      // becomes the single canonical read path in fixed mode.
      patch.feePct = null;
    }
    if (typeof onSave === "function") onSave(patch);
    onOpenChange?.(false);
  };

  return (
    <Sheet open={!!open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[440px] overflow-y-auto"
        data-testid="financials-sheet"
      >
        <SheetHeader className="mb-5">
          <div className="inline-flex items-center gap-2">
            <span
              className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-violet-50 text-violet-600"
              aria-hidden
            >
              <Coins size={15} strokeWidth={2} />
            </span>
            <SheetTitle className="text-[16px] font-bold tracking-tight text-slate-900">
              Edit financials
            </SheetTitle>
          </div>
          <SheetDescription className="text-[12.5px] text-slate-500 leading-snug">
            Live transaction commercials. These feed Forecasting Centre revenue projections and at-risk calculations.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4">
          {/* Agreed sale price */}
          <Field
            label="Agreed sale price"
            value={form.agreedPrice}
            onChange={(v) => set("agreedPrice", v)}
            placeholder="1,400,000"
            currency
            testid="financials-agreed-price"
          />

          {/* Commission type — segmented.  Switching modes
              preserves the unsaved draft in the inactive mode so
              the operator can toggle without losing typed values. */}
          <div>
            <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">
              Commission type
            </label>
            <div
              className="grid grid-cols-2 gap-1.5 rounded-xl bg-slate-50/70 ring-1 ring-slate-200/70 p-1"
              data-testid="financials-commission-type"
              role="tablist"
              aria-label="Commission type"
            >
              {COMMISSION_TYPES.map((opt) => {
                const active = form.commissionType === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => set("commissionType", opt.key)}
                    className={`h-9 rounded-lg text-[12.5px] font-semibold transition-colors ${
                      active
                        ? "bg-white shadow-sm ring-1 ring-slate-200/80 text-slate-900"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid={`financials-commission-type-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mode-scoped commission inputs. */}
          {isPct ? (
            <div
              className="grid grid-cols-2 gap-3"
              data-testid="financials-mode-percentage"
            >
              <Field
                label="Commission (%)"
                value={form.commissionPct}
                onChange={(v) => set("commissionPct", v)}
                placeholder="1.25"
                type="number"
                step="0.05"
                min="0"
                max="100"
                testid="financials-commission-pct"
              />
              <div>
                <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">
                  Commission (£)
                </label>
                <div
                  className="w-full h-10 px-3 rounded-xl bg-slate-50 ring-1 ring-slate-200 text-[13px] text-slate-700 font-semibold tabular-nums flex items-center"
                  data-testid="financials-commission-gbp"
                  aria-readonly="true"
                >
                  {derivedCommissionGbp != null ? _fmtGbp(derivedCommissionGbp) : "—"}
                </div>
              </div>
            </div>
          ) : (
            <div data-testid="financials-mode-fixed">
              <Field
                label="Fixed fee"
                value={form.fixedFee}
                onChange={(v) => set("fixedFee", v)}
                placeholder="15,000"
                currency
                testid="financials-fixed-fee"
              />
            </div>
          )}

          {/* VAT included toggle */}
          <label
            className="flex items-center justify-between gap-3 rounded-xl bg-slate-50/70 ring-1 ring-slate-200/70 px-3.5 py-2.5 cursor-pointer"
            data-testid="financials-vat-toggle"
          >
            <span className="text-[12.5px] font-semibold text-slate-700">VAT included</span>
            <input
              type="checkbox"
              checked={!!form.vatIncluded}
              onChange={(e) => set("vatIncluded", e.target.checked)}
              className="h-4 w-4 accent-violet-600"
              data-testid="financials-vat-input"
            />
          </label>

          {/* Expected exchange month.
              iter-alignment · P0 · Financials Refinement (2026-02-28)
              — the native `<input type="month">` remains the write
              surface (keeps the stored `YYYY-MM-15` value untouched)
              and a UK-readable hint (`September 2026`) appears
              directly beneath so operators always see a natural
              month + year rendering. */}
          <div>
            <Field
              label="Expected exchange month"
              value={form.expectedExchangeMonth}
              onChange={(v) => set("expectedExchangeMonth", v)}
              placeholder="YYYY-MM"
              type="month"
              testid="financials-expected-month"
            />
            {form.expectedExchangeMonth && (
              <div
                className="mt-1 text-[11px] text-slate-500 tabular-nums"
                data-testid="financials-expected-month-readable"
              >
                {(() => {
                  const [y, m] = String(form.expectedExchangeMonth).split("-");
                  if (!y || !m) return null;
                  const d = new Date(Number(y), Number(m) - 1, 1);
                  return Number.isFinite(d.getTime())
                    ? d.toLocaleString("en-GB", { month: "long", year: "numeric" })
                    : null;
                })()}
              </div>
            )}
          </div>

          {/* Revenue at risk segmented */}
          <div>
            <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">
              Revenue at risk
            </label>
            <div
              className="grid grid-cols-3 gap-1.5 rounded-xl bg-slate-50/70 ring-1 ring-slate-200/70 p-1"
              data-testid="financials-risk-segments"
            >
              {RISK_OPTIONS.map((opt) => {
                const active = form.revenueAtRisk === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => set("revenueAtRisk", opt)}
                    className={`h-9 rounded-lg text-[12.5px] font-semibold transition-colors ${
                      active
                        ? "bg-white shadow-sm ring-1 ring-slate-200/80 text-slate-900"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid={`financials-risk-${opt.toLowerCase()}`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-7 flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            className="inline-flex items-center justify-center h-10 px-4 rounded-xl bg-white ring-1 ring-slate-200 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 cf-press"
            data-testid="financials-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isValid}
            aria-disabled={!isValid}
            className={`inline-flex items-center justify-center h-10 px-4 rounded-xl text-[13px] font-semibold text-white shadow-[0_1px_2px_rgba(124,58,237,0.18),0_6px_14px_-8px_rgba(124,58,237,0.40)] cf-press ${
              isValid
                ? "bg-violet-600 hover:bg-violet-700"
                : "bg-violet-300 cursor-not-allowed"
            }`}
            data-testid="financials-save"
          >
            Save financials
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, step, min, max, currency = false }) {
  if (currency) {
    return (
      <div>
        <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">
          {label}
        </label>
        <div className="relative">
          <span
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-slate-500 tabular-nums"
            aria-hidden
          >
            £
          </span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={value}
            onChange={(e) => onChange(_formatCurrencyInput(e.target.value))}
            placeholder={placeholder}
            className="w-full h-10 pl-7 pr-3 rounded-xl bg-white ring-1 ring-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 tabular-nums"
            data-testid={testid}
          />
        </div>
      </div>
    );
  }
  return (
    <div>
      <label className="block text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        step={step}
        min={min}
        max={max}
        className="w-full h-10 px-3 rounded-xl bg-white ring-1 ring-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
        data-testid={testid}
      />
    </div>
  );
}

export default FinancialsSheet;
